import { EEntityType, ELinks, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import { generateFullName } from '@sharedInterfaces/globalHelper';
import type { IOU } from '@sharedInterfaces/ICompanySettings';
import type {
    IOrgUnitDetailDTO,
    IOrgUnitSalesDataDTO,
    IOUAvailabilityDataEmployeesDTO,
} from '@sharedInterfaces/IOrgUnit';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import getOrgUnit from '@src/APIs/graphQl/OrgUnit/getOrgUnit';
import getOrgUnitAvailabilityData from '@src/APIs/graphQl/OrgUnit/getOrgUnitAvailabilityData';
import getOrgUnitSalesData from '@src/APIs/graphQl/OrgUnit/getOrgUnitSalesData';
import SyncTimeButton from '@src/App/Components/Buttons/SyncTimeButton/SyncTimeButton';
import EmployeesCard from '@src/App/Components/Cards/EntityCards/Employees/EmployeesCard/EmployeesCard';
import SkillCategoriesCard from '@src/App/Components/Cards/EntityCards/SkillCategories/SkillCategoriesCard/SkillCategoriesCard';
import SkillsCard from '@src/App/Components/Cards/EntityCards/Skills/SkillsCard/SkillsCard';
import SubordinateOrgUnitsCard from '@src/App/Components/Cards/EntityCards/SubordinateOrgUnits/SubordinateOrgUnitsCard/SubordinateOrgUnitsCard';
import EntityPageInformation from '@src/App/Components/Core/Layouts/LayoutElements/EntityPageInformation/EntityPageInformation';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import type { AppState } from '@store/store';
import { Building, Settings, Users } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useNavigate, useParams } from 'react-router-dom';

import EntityPage from '../../Generic/EntityPage/EntityPage';

import { AvailabilitiesContent } from './AvailabilitiesContent';
import { SalesContent } from './SalesContent';

interface OrgUnitPageProps {
    entity?: IOrgUnitDetailDTO;
    inDialog?: boolean;
}

/**
 * Represents the OrgUnitPage component.
 *
 * @param {OrgUnitPageProps} props - The props for the OrgUnitPage component.
 * @returns {JSX.Element} The JSX element representing the OrgUnitPage component.
 */
function OrgUnitPage(props: OrgUnitPageProps) {
    const { id, tab } = useParams();
    const navigate = useNavigate();
    const entityId = props.entity ? props.entity.id : id ? Number.parseInt(id, 10) : -1;
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).orgUnit;
    const OUs = useSelector((state: AppState) => state.OUs);
    const permissions = useSelector((state: AppState) => state.permissions);
    const entityState = useSelector((state: AppState) => state.orgUnits.find((a) => a.id === entityId)); //TODO: prüfen ob == zu === zu problemen führt
    const [activeRole, setActiveRole] = useState('hr');

    const [salesData, setSalesData] = useState<IOrgUnitSalesDataDTO | null>(null);
    const [availabilityData, setAvailabilityData] = useState<IOUAvailabilityDataEmployeesDTO | null>(null);
    const [loadingSalesData, setLoadingSalesData] = useState<boolean>(false);
    const [loadingAvailabilityData, setLoadingAvailabilityData] = useState<boolean>(false);
    const [parentOU, setParentOU] = useState({} as IOU);
    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);

    useEffect(() => {
        if (entityId === -1) return;
        setSalesData(null);
        setAvailabilityData(null);
        setLoadingAvailabilityData(false);
        setLoadingSalesData(false);
    }, [entityId]);

    useEffect(() => {
        if (props.inDialog) return;
        if (entityId === -1) return;

        getOrgUnit(entityId).catch((ex) => {
            setLoadingError(ex);
        });
    }, [entityId, props.inDialog]);

    useEffect(() => {
        const loadedOU = OUs.find((cat) => cat.id === entityId);
        if (loadedOU?.parentOU) {
            const parentOU = OUs.find((cat) => cat.id === loadedOU.parentOU);
            if (parentOU) setParentOU(parentOU);
            else setParentOU({} as IOU);
        } else setParentOU({} as IOU);
    }, [OUs, entityId]);

    const loadSalesData = useCallback(() => {
        if (salesData) return;
        if (loadingSalesData) return;
        const salesDataT = localStorage.getItem(`OrgSalesData-${entityId}`);
        if (salesDataT) {
            setSalesData(JSON.parse(salesDataT));
        }
        setLoadingSalesData(true);
        getOrgUnitSalesData(entityId, (details) => {
            setSalesData(details);
            //Save in Local Storage with "OrgSalesData-OrgId" als Key
            localStorage.setItem(`OrgSalesData-${entityId}`, JSON.stringify(details));
        }).catch((ex) => {
            setLoadingError(ex);
        });
    }, [entityId, salesData, loadingSalesData]);
    const loadSalesAvailability = useCallback(() => {
        if (availabilityData) return;
        if (loadingAvailabilityData) return;
        const availabilityDataT = localStorage.getItem(`OrgAvailabilityData-${entityId}`);
        if (availabilityDataT) {
            const tempData = JSON.parse(availabilityDataT) as IOUAvailabilityDataEmployeesDTO;
            tempData.employees.forEach((data) => {
                data.lastForecastDate = new Date(data.lastForecastDate);
            });
            setAvailabilityData(tempData);
        }
        setLoadingAvailabilityData(true);
        getOrgUnitAvailabilityData(entityId, (details) => {
            setAvailabilityData(details);
            //Save in Local Storage with "OrgAvailabilityData-OrgId" als Key
            localStorage.setItem(`OrgAvailabilityData-${entityId}`, JSON.stringify(details));
        }).catch((ex) => {
            setLoadingError(ex);
        });
    }, [entityId, availabilityData, loadingAvailabilityData]);
    useEffect(() => {
        if (props.inDialog) return;
        if (entityId === -1) return;
        if (OUs.length === 0) return;
        if (tab === 'sales') {
            loadSalesData();
        } else if (tab === 'availabilities') {
            loadSalesAvailability();
        }
    }, [OUs, entityId, props.inDialog, tab, loadSalesData, loadSalesAvailability]);

    const allOrgUnits = useSelector((state: AppState) => state.orgUnits);
    const subordinateOUs = entityState ? allOrgUnits.filter((ou) => ou.parentOU === entityState.id) : [];

    const dashboardContent = (
        <>
            {entityState ? (
                <>
                    <EmployeesCard
                        employees={entityState.employees.map((e) => ({
                            id: e.id,
                            title: generateFullName(e.firstname, e.lastname),
                        }))}
                        inOverlay={props.inDialog}
                    />
                    {subordinateOUs.length > 0 && <SubordinateOrgUnitsCard subordinateOrgUnits={subordinateOUs} />}
                    <SkillCategoriesCard skillCategories={entityState.skillCategories} />
                    <SkillsCard skills={entityState.skills} />
                </>
            ) : (
                <></>
            )}
        </>
    );

    let content = dashboardContent;
    if (tab === 'sales' && checkPermissions('OrgUnits', 'RetrieveSalesData', permissions)) {
        content = (
            <SalesContent
                salesData={salesData}
                refresh={() => {
                    setSalesData(null);
                    setLoadingSalesData(false);
                    setTimeout(() => {
                        loadSalesData();
                    }, 500);
                }}
            />
        );
    } else if (tab === 'availabilities' && entityState) {
        content = (
            <AvailabilitiesContent
                allowSales={checkPermissions('OrgUnits', 'RetrieveSalesData', permissions)}
                availabilityData={availabilityData}
            />
        );
    }
    const roles: IMvpRole[] = [
        {
            id: 'hr',
            label: 'HR',
            icon: <Users size={18} />,
        },
        {
            id: 'admin',
            label: 'Admin',
            icon: <Settings size={18} />,
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'all-orgunits',
            label: 'Alle Abteilungen',
            icon: <Building size={18} />,
            onClick: () => navigate(`/${ELinks.PEOPLE_ORGUNITS}`),
            variant: 'primary',
        },
        {
            id: 'edit-orgunit',
            label: 'Bearbeiten',
            icon: <Settings size={18} />,
            onClick: () => console.log('Edit org unit'),
        },
    ];

    if (props.inDialog) {
        return (
            <EntityPage
                inDialog={props.inDialog}
                entity={entityState ? entityState : undefined}
                admin={permissions.admin}
                permissions={permissions.entities.SkillCategories}
                loadingError={loadingError}
                menuItems={permissions.admin ? [<SyncTimeButton key={'timeSync'} />] : undefined}
                tabs={
                    props.inDialog
                        ? undefined
                        : [
                              {
                                  title: langStrings.dashboard,
                                  link: `/${ELinks.PEOPLE_ORGUNITS}/${id}/dashboard`,
                              },
                              {
                                  title: langStrings.availabilities,
                                  link: `/${ELinks.PEOPLE_ORGUNITS}/${id}/availabilities`,
                              },
                              ...(checkPermissions('OrgUnits', 'RetrieveSalesData', permissions)
                                  ? [
                                        {
                                            title: langStrings.salesDataLink,
                                            link: `/${ELinks.PEOPLE_ORGUNITS}/${id}/sales`,
                                        },
                                    ]
                                  : []),
                          ]
                }
                informations={
                    entityState
                        ? [
                              entityState.leader.id !== -1 ? (
                                  <EntityPageInformation key="leader" title={langStrings.leader}>
                                      <NavLink to={entityTypeToLink(entityState.leader.id, EEntityType.EMPLOYEE)}>
                                          {generateFullName(entityState.leader.firstname, entityState.leader.lastname)}
                                      </NavLink>
                                  </EntityPageInformation>
                              ) : undefined,
                              parentOU.id ? (
                                  <EntityPageInformation key="parentOU" title={langStrings.parentOU}>
                                      <NavLink to={`/${ELinks.PEOPLE_ORGUNITS}/${parentOU.id}`}>
                                          {parentOU.title}
                                      </NavLink>
                                  </EntityPageInformation>
                              ) : undefined,
                              <EntityPageInformation key="allOrgUnits" title={langStrings.oftenUsed.orgUnits}>
                                  <NavLink to={`/${ELinks.PEOPLE_ORGUNITS}`}>{langStrings.oftenUsed.overview}</NavLink>
                              </EntityPageInformation>,
                          ].filter(Boolean)
                        : []
                }
            >
                {content}
            </EntityPage>
        );
    }

    return (
        <>
            <MvpSubNav
                currentPageTitle={entityState?.title || 'Organisationseinheit'}
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />
            <EntityPage
                inDialog={props.inDialog}
                entity={entityState ? entityState : undefined}
                admin={permissions.admin}
                permissions={permissions.entities.SkillCategories}
                loadingError={loadingError}
                menuItems={permissions.admin ? [<SyncTimeButton key={'timeSync'} />] : undefined}
                tabs={
                    props.inDialog
                        ? undefined
                        : [
                              {
                                  title: langStrings.dashboard,
                                  link: `/${ELinks.PEOPLE_ORGUNITS}/${id}/dashboard`,
                              },
                              {
                                  title: langStrings.availabilities,
                                  link: `/${ELinks.PEOPLE_ORGUNITS}/${id}/availabilities`,
                              },
                              ...(checkPermissions('OrgUnits', 'RetrieveSalesData', permissions)
                                  ? [
                                        {
                                            title: langStrings.salesDataLink,
                                            link: `/${ELinks.PEOPLE_ORGUNITS}/${id}/sales`,
                                        },
                                    ]
                                  : []),
                          ]
                }
                informations={
                    entityState
                        ? [
                              entityState.leader.id !== -1 ? (
                                  <EntityPageInformation key="leader" title={langStrings.leader}>
                                      <NavLink to={entityTypeToLink(entityState.leader.id, EEntityType.EMPLOYEE)}>
                                          {generateFullName(entityState.leader.firstname, entityState.leader.lastname)}
                                      </NavLink>
                                  </EntityPageInformation>
                              ) : undefined,
                              parentOU.id ? (
                                  <EntityPageInformation key="parentOU" title={langStrings.parentOU}>
                                      <NavLink to={`/${ELinks.PEOPLE_ORGUNITS}/${parentOU.id}`}>
                                          {parentOU.title}
                                      </NavLink>
                                  </EntityPageInformation>
                              ) : undefined,
                              <EntityPageInformation key="allOrgUnits" title={langStrings.oftenUsed.orgUnits}>
                                  <NavLink to={`/${ELinks.PEOPLE_ORGUNITS}`}>{langStrings.oftenUsed.overview}</NavLink>
                              </EntityPageInformation>,
                          ].filter(Boolean)
                        : []
                }
            >
                {content}
            </EntityPage>
        </>
    );
}

export default OrgUnitPage;
